// Feature spec 17, phase 3: the Entry points tab's list, form and dialogs, the Overview band and the deployment side
// (spec 11 6.15.5, 6.15.10, 6.15.28–30).
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const usd = (currency: string, n: number) => `${currency} ${n.toLocaleString('en-US')}`;

export const networkEntryPoints = {
  // 6.15.10 List
  summary: (eps: number, routes: number) => `${plural(eps, 'entry point')} · ${plural(routes, 'route')}`,
  kindAndTier: (kind: string, tier: string | null) => (tier ? `${kind} · ${tier}` : kind),
  regionHub: (region: string) => `${region} hub`,
  notApplied: 'Not applied yet',
  changed: 'Changed since the last apply',
  copyHost: 'Copy host name',
  copyIp: 'Copy public IP',
  actions: (id: string) => `Actions for entry point ${id}`,
  edit: 'Edit…',
  rotate: 'Rotate origin secret…',
  remove: 'Remove…',
  refresh: 'Refresh status',
  expand: (id: string) => `Show entry point ${id}`,
  collapse: (id: string) => `Hide entry point ${id}`,
  statusUnknown: 'Status unknown',
  chips: {
    wafPrevention: 'WAF prevention',
    wafDetection: 'WAF detection',
    wafDetectionHint: 'WAF in detection mode: requests aren’t blocked',
    managedRules: 'managed rules',
    cdnOff: 'CDN off',
    cdnOn: 'CDN caching',
    privateOrigins: 'Private origins',
    ddosIp: 'DDoS IP Protection',
    ddosNetwork: 'DDoS Network Protection',
  },
  domains: 'Domains',
  domainsCaption: (id: string) => `Domains of entry point ${id}`,
  hostName: 'Host name',
  state: 'State',
  next: 'What to do',
  domainState: {
    validated: 'Validated',
    'waiting-for-dns': 'Waiting for DNS',
    pending: 'Pending (not applied)',
    failed: 'Validation failed',
    unknown: 'Unknown',
  } as Record<string, string>,
  managedCertificate: 'managed certificate',
  keyVaultCertificate: 'Key Vault certificate',
  renews: 'renews automatically',
  lastKnown: (time: string) => `last known ${time}`,
  createVerb: 'Create',
  copyRecord: (type: string, name: string) => `Copy the ${type} record of ${name}`,
  appliesFirst: 'Shown after the first apply',
  routes: 'Routes',
  routesCaption: (id: string) => `Routes of entry point ${id}`,
  hostAndPath: 'Host and path',
  origin: 'Origin',
  originAccess: 'Origin access',
  health: 'Health',
  edgeHost: '(edge host name)',
  allHosts: 'all domains',
  noRoutes: 'No routes yet: the edge answers nothing until a route sends requests to an app.',
  healthWords: {
    healthy: 'Healthy',
    unhealthy: 'Unhealthy',
    'not-probed': 'Not probed',
    unknown: 'Unknown',
  } as Record<string, string>,
  notProbedHint: 'Front Door doesn’t probe single-origin groups',
  waitingForApply: 'Waiting for apply',
  notAppliedRoute: 'Not applied',
  inactive: (reason: string) => `Inactive: ${reason}`,
  crossCloud: { aws: 'AWS', gcp: 'Google Cloud' } as Record<string, string>,
  pendingOrigins: (names: string) =>
    `Waiting for ${names} to be applied: its app isn’t locked to this edge yet.`,
  certificateAccess: (vault: string, principal: string, network: string) =>
    `Grant Key Vault Secrets User on ${vault} to the gateway’s identity ${principal}, then apply ${network}.`,
  copyPrincipal: 'Copy the identity’s principal ID',
  rotation: (route: string) => `Rotating the origin secret of route ${route}:`,
  rotationStep: {
    requested: (network: string) => `waits for ${network}’s next apply`,
    delivering: 'waiting for',
    deliveringAfter: 'to be applied',
    switching: (network: string) => `waits for ${network}’s next apply, which switches the edge`,
    dropping: (network: string) => `the old secret is removed with ${network}’s next apply`,
  },
  stale: (time: string) => `The status couldn’t be read; these are the last known states from ${time}.`,
  appGatewayHub: (region: string) => `Azure Application Gateway · WAF_v2 · ${region} hub`,
  // 6.15.10 Form
  newTitle: (kind: string) => `New entry point · ${kind}`,
  editTitle: (id: string) => `Edit entry point ${id}`,
  name: 'Name',
  id: 'ID',
  idHelp: 'Lowercase letters, digits and hyphens; fixed once saved.',
  idInvalid: 'Use lowercase letters, digits and hyphens, starting with a letter (at most 31).',
  idTaken: (id: string) => `Another entry point is called ${id}.`,
  label: 'Label (optional)',
  tier: 'Tier',
  tierCost: (label: string, currency: string, monthly: number) =>
    `${label} · ${usd(currency, monthly)}/month`,
  premiumExtras: 'managed WAF rules, bot protection, Private Link origins',
  region: 'Region',
  regionHelp: 'The gateway sits in this region’s hub; routes reach apps attached in this region.',
  addOns: 'Add-ons',
  addOnCost: (currency: string, monthly: number) => `${usd(currency, monthly)}/month`,
  perPublicIp: 'per public IP',
  needsTier: (tier: string) => `Needs ${tier}`,
  wafMode: 'Mode',
  prevention: 'Prevention',
  detection: 'Detection',
  detectionHelp: 'Requests aren’t blocked; use it for a first rollout.',
  managedRules: 'Managed rules',
  botProtection: 'Bot protection',
  managedNeedsPremium: 'Managed rules and bot protection need Premium.',
  rateLimit: 'Rate limit per client IP and minute',
  rateLimitHelp: '0 turns the rate limit off; otherwise 100 to 100,000.',
  rateLimitInvalid: 'Enter 0 or a number from 100 to 100,000.',
  ddosExclusive: 'DDoS IP Protection and DDoS Network Protection exclude each other.',
  domainsHelp: 'Host names customers use; each needs a certificate.',
  domainHost: (i: number) => `Host name of domain ${i}`,
  domainCertificate: (i: number) => `Certificate of domain ${i}`,
  secretId: (i: number) => `Key Vault secret ID of domain ${i}`,
  managed: 'Managed',
  keyVault: 'Key Vault',
  keyVaultOnly:
    'The gateway reads the certificate with its own identity; after the first apply the entry point shows the identity to grant Key Vault Secrets User.',
  addDomain: 'Add domain',
  removeDomain: (host: string) => `Remove domain ${host || 'without a name'}`,
  hostInvalid: 'Enter a host name such as shop.example.com.',
  hostDuplicate: 'This host name is used twice.',
  secretIdRequired: 'Enter the certificate’s Key Vault secret ID.',
  routesHelp: 'Each route sends requests for its hosts and paths to one app.',
  route: (i: number) => `Route ${i}`,
  routeId: (i: number) => `ID of route ${i}`,
  routeHosts: (i: number) => `Host names of route ${i}`,
  allDomains: 'All domains',
  edgeOwnHost: 'The edge’s own host name',
  routePaths: (i: number) => `Paths of route ${i}`,
  pathsHelp: 'Comma-separated; /* for everything, /api/* for a prefix, or an exact path.',
  pathInvalid: 'Paths start with / and only end in * after a /.',
  routeOrigin: (i: number) => `Origin of route ${i}`,
  chooseOrigin: 'Choose an app…',
  noOrigins: (network: string) => `Attach a deployment to ${network} to route to its apps.`,
  originMissing: 'Choose an app.',
  inRegion: (region: string) => `Apps attached in ${region}`,
  routeAccess: (i: number) => `Origin access of route ${i}`,
  notLocked: 'Not locked',
  strength: {
    strongest: 'strongest',
    strong: 'strong',
    medium: 'medium',
    weak: 'weak',
    none: 'none',
  } as Record<string, string>,
  accessLabel: {
    'private-link': 'Private Link',
    'private-endpoint': 'Private endpoint',
    'vpc-origin': 'VPC origin',
    'serverless-neg': 'Serverless NEG',
    'access-restriction': 'Access restriction',
    'allow-list-and-header': 'Edge ranges and secret header',
    header: 'Secret header',
    none: 'Not locked',
  } as Record<string, string>,
  accessSentence: {
    'private-link':
      'Private Link: the app accepts only this Front Door, privately; the app’s next apply approves the connection.',
    'private-endpoint': 'Private endpoint: the gateway reaches the app privately through the hub.',
    'vpc-origin': 'VPC origin: the edge reaches the app privately.',
    'serverless-neg': 'Serverless NEG: the load balancer reaches the service directly.',
    'access-restriction':
      'Access restriction: the app accepts only Front Door’s service tag with this Front Door’s ID.',
    'allow-list-and-header':
      'The edge’s address ranges plus a secret header: the app must check X-Nebisys-Origin against NEBISYS_ORIGIN_SECRET.',
    header: 'A secret header only: the app must check X-Nebisys-Origin against NEBISYS_ORIGIN_SECRET.',
    none: 'Not locked: callers can reach the app directly and bypass the WAF.',
  } as Record<string, string>,
  mustChoose:
    'No lock-down is preselected for this platform: choose one, knowing what the weaker ones allow.',
  removeRoute: (id: string) => `Remove route ${id || 'without an ID'}`,
  addRoute: 'Add route',
  push: 'Keep Event Grid and SignalR pushes',
  pushHelp: (app: string) =>
    `Every route of ${app} becomes reachable from any customer’s Event Grid or SignalR service without the WAF; separate function apps for push triggers and edge routes avoid this.`,
  costs: 'Costs',
  costItem: (item: string, currency: string, monthly: number) => `${item} ${usd(currency, monthly)}/month`,
  costLine: (items: string, asOf: string) =>
    `Fixed costs: ${items} (list price, as of ${asOf}, without traffic)`,
  noFixedCost: 'No fixed cost',
  costsPending: 'Costs follow the check of your changes.',
  checking: 'Checking…',
  impactUnknown: 'Impact unknown: the check failed.',
  save: 'Save',
  saving: 'Saving…',
  saved: (network: string, id: string) => `Saved. Apply ${network} to create ${id}.`,
  savedEdit: (network: string, id: string) => `Saved. Apply ${network} to update ${id}.`,
  conflict:
    'Someone else changed this network’s entry points; your entry point was merged into theirs. Check it and save again.',
  fixErrors: (n: number) => `${plural(n, 'error')} to fix before saving.`,
  // Remove
  removeTitle: (id: string) => `Remove entry point ${id}?`,
  removeBody: (id: string) => `Traffic through entry point ${id} stops with the network’s next apply.`,
  removeOrigins: (names: string) =>
    `Its origins (${names}) keep their lock-down until their next apply, so they stay unreachable directly.`,
  removeConfirm: 'Remove entry point',
  removed: (network: string, id: string) => `Removed ${id}. Apply ${network} to delete it in the cloud.`,
  // Rotate
  rotateTitle: (id: string) => `Rotate the origin secret of ${id}`,
  rotateRoute: 'Route',
  allRoutes: 'All routes with a secret',
  rotateSteps: (network: string) => [
    `${network}’s next apply adds a new secret while the edge keeps sending the current one.`,
    'Each origin deployment’s next apply accepts both.',
    `${network}’s next apply then switches the edge; the old secret is removed afterwards.`,
  ],
  rotateNoValue: 'No secret value is ever shown or copied.',
  startRotation: 'Start rotation',
  rotationStarted: 'Rotation requested.',
  noSecretRoutes: 'No route of this entry point uses an origin secret.',
  // 6.15.5 Overview
  band: 'Entry points',
  bandNone: 'No entry points.',
  bandCard: (id: string, kind: string, tier: string | null, waf: string) =>
    [id, kind + (tier ? ` ${tier}` : ''), waf].filter(Boolean).join(', '),
  pendingCount: (n: number) => `${plural(n, 'origin')} waiting for apply`,
  treeItem: (id: string, kind: string, waf: string, routes: number, pending: number) =>
    [
      id,
      kind,
      waf,
      plural(routes, 'route'),
      pending ? `${plural(pending, 'origin')} waiting for apply` : null,
    ]
      .filter(Boolean)
      .join(', '),
  wafWords: (mode: string | null) =>
    mode === 'detection' ? 'WAF detection' : mode === 'prevention' ? 'WAF prevention' : 'no WAF',
  entryRoutes: (n: number) => (n === 1 ? '1 entry route' : `${n} entry routes`),
  entryRouteLine: (route: string, ep: string, resource: string, access: string, state: string) =>
    `${route} (${ep}) → ${resource} · ${access} · ${state}`,
  // 6.15.28 Deployment side
  cardTitle: 'Entry routes',
  originState: {
    applied: 'applied',
    'waiting-for-apply': 'waiting for apply',
    inactive: 'inactive',
    'not-applied': 'not applied yet',
  } as Record<string, string>,
  applyToLock: (deployment: string, resource: string) =>
    `Apply ${deployment} to lock ${resource} to its edge`,
  designerBadge: (abbr: string, ep: string, access: string) => `${abbr} ${ep} · ${access}`,
  originSecretRow: 'Entry point origin secret',
  activityKinds: {
    'network-origin-rotation-requested': 'Origin secret rotation requested',
    'network-origin-switched': 'Origin secret switched',
    'network-origin-regenerated': 'Origin secret replaced',
  } as Record<string, string>,
  activity: {
    requested: 'requested a rotation of the origin secret of route',
    switched: (network: string) => `${network}’s apply switched route`,
    toNew: 'to a new origin secret',
    regenerated: (network: string) => `${network}’s apply replaced a lost origin secret of route`,
  },
  // 6.15.29 Errors
  codes: {
    ROUTE_ORIGIN_NOT_ATTACHED: (deployment: string, network: string) =>
      `Attach ${deployment} to ${network} first`,
    ROUTE_ORIGIN_UNKNOWN: 'This app no longer exists; choose another origin',
    AZURE_APP_GATEWAY_HUB_TOO_SMALL:
      'This region’s hub is too small for a gateway: use a network with /22 hubs',
    CERTIFICATE_ACCESS_PENDING: (principal: string) =>
      `Grant Key Vault Secrets User to the gateway’s identity ${principal}, then apply the network`,
    ORIGIN_SECRET_UNAVAILABLE: (network: string) =>
      `Ask a network admin to apply ${network}; its apply creates a new origin secret`,
    ORIGIN_SECRET_REGENERATED: (route: string, deployment: string) =>
      `Requests through route ${route} fail until ${deployment} is applied`,
    ORIGIN_ROTATION_PENDING: 'A rotation of this route is still running; it finishes with the next applies.',
    ORIGIN_SECRET_NOT_USED: 'No route of this entry point uses an origin secret.',
    CONNECTION_THROUGH_EDGE: (app: string, target: string, ep: string, url: string) =>
      `${app} reaches ${target} through entry point ${ep} at ${url}`,
    ORIGIN_UNEXPECTED_PRIVATE_ENDPOINT:
      'An unexpected private endpoint connection on this app was rejected; only the network’s Front Door may connect.',
  },
};
